/**
 * FAS-113: Observability Route Guard
 * 
 * Allows access to observability pages for:
 * - Super Admins (platform role)
 * - Any of the 5 business roles (firm_owner, admin, attorney, paralegal, support)
 * 
 * Redirects:
 * - Unauthenticated users → /auth
 * - Users without valid role → /dashboard
 */
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

interface ObservabilityGuardProps {
  children: ReactNode;
}

/** 
 * All 5 business roles that can view observability dashboards (read-only for non-admin roles).
 */
const OBSERVABILITY_ALLOWED_ROLES = ['firm_owner', 'admin', 'attorney', 'paralegal', 'support'] as const;

export function ObservabilityGuard({ children }: ObservabilityGuardProps) {
  const { isSuperAdmin, loading, isAuthenticated, activeBusiness } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Super Admins always have full access
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check if user has one of the 5 business roles
  const hasValidRole = activeBusiness?.role && 
    OBSERVABILITY_ALLOWED_ROLES.includes(activeBusiness.role as typeof OBSERVABILITY_ALLOWED_ROLES[number]);

  if (!hasValidRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
